import { use } from "react";
import { useNavigate, useSearchParams } from "react-router";
import NoData from "../../assets/no-data.svg?react";
import Button from "../../components/Button";
import Icon from "../../components/Icon";
import Panel from "../../components/Panel";
import UpgradeDialog from "../../components/UpgradeDialog";
import { BikeContext } from "../../context/bikeContext";
import { useEntitlements } from "../../hooks/useAccount";
import { useSetups2 } from "../../hooks/useSetups";
import useToggle from "../../hooks/useToggle";
import { isEntitledTo, Setup } from "../../http/onlyclicksApi";
import styles from "./FilterableSetupList.module.css";
import SetupList from "./SetupList";

interface Props {
  onClick?: (setup: Setup) => void;
}

const FilterableSetupList = ({ onClick }: Props) => {
  const { bike } = use(BikeContext);
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") ?? undefined;

  const {
    data: setups,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
  } = useSetups2(bike?.id, searchQuery);

  const noSetups = !isPending && setups && Object.keys(setups).length === 0;

  return (
    <>
      <Panel title="Your setups">
        <SetupList setups={setups} onClick={onClick} />
        {hasNextPage && (
          <button
            className={styles.loadMore}
            onClick={() => {
              fetchNextPage();
            }}
            disabled={isFetchingNextPage}
          >
            Load more
          </button>
        )}
        {noSetups && !searchQuery && <SetupOnboard />}
        {noSetups &&
          searchQuery &&
          "No setups were found matching your search query."}
      </Panel>
    </>
  );
};

const SetupOnboard = () => {
  const navigate = useNavigate();
  const { data: entitlements } = useEntitlements();
  const upgradeDialog = useToggle();

  return (
    <>
      <div className={styles.onboard}>
        You have no setups
        <Icon glyph={NoData} className={styles.noDataIcon} />
        <Button
          type="accent"
          fullWidth={true}
          buttonProps={{
            onClick: () => {
              isEntitledTo(entitlements!.setups)
                ? navigate("/setups/new")
                : upgradeDialog.open();
            },
          }}
        >
          Get started now
        </Button>
      </div>
      <UpgradeDialog
        open={upgradeDialog.isOpen}
        onClose={upgradeDialog.close}
      />
    </>
  );
};

export default FilterableSetupList;
